import { ReactNode } from 'react';
import { format } from 'date-fns';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import Card from '../../components/ui/Card';
import ResourceCard from '../../components/resources/ResourceCard';
import { WPMedia, WPPost } from '../../types/wordpress';
import { decodeHtmlEntities, stripHtml } from '../../utils/htmlEntities';
import { exampleOptions } from './cardReviewStorage';
import { resourceScent } from './scentViews';
import { ExampleSwitcher, FamilyBlock, ReviewFrame, columnCard } from './reviewUi';

function titleOf(post: WPPost) {
  return decodeHtmlEntities(post.title.rendered);
}

function excerptOf(post: WPPost) {
  return stripHtml(post.excerpt.rendered);
}

export default function ResourceReview({
  post,
  attachments,
  notice,
  error,
  choices,
  onChoose,
  notes,
  onNote,
}: {
  post: WPPost | null;
  attachments: WPMedia[];
  notice: string | null;
  error: string | null;
  choices: Record<string, string>;
  onChoose: (exampleId: string, optionId: string) => void;
  notes: Record<string, string>;
  onNote: (exampleId: string, note: string) => void;
}) {
  if (!post) {
    return (
      <FamilyBlock
        id="review-resources"
        title="Resources"
        judge="Type rail draws a resource as a page with a gray footer for the date, revision, and files."
        destination="A resource card opens the document."
        notice={notice}
        error={error}
      >
        {null}
      </FamilyBlock>
    );
  }

  const title = titleOf(post);
  const excerpt = excerptOf(post);
  const examples: { id: string; label: string; current: (frame: ReviewFrame) => ReactNode }[] = [
    { id: 'resources-list', label: 'Library list', current: () => <ResourceCard post={post} view="list" attachments={attachments} /> },
    {
      id: 'resources-grid',
      label: 'Library grid',
      current: (frame) => columnCard(frame, <ResourceCard post={post} view="grid" attachments={attachments} />),
    },
    { id: 'resources-search', label: 'Search results', current: () => <ResourceCard post={post} view="list" /> },
    {
      id: 'resources-scan',
      label: 'Program scan',
      current: () => <ResourceCard post={post} view="list" attachments={attachments} summarizeAttachments />,
    },
    {
      id: 'resources-related',
      label: 'Related resources',
      current: (frame) =>
        columnCard(
          frame,
          <Card hover>
            <h4 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">{title}</h4>
            <p className="text-sm text-gray-600 mb-3 line-clamp-3">{excerpt.substring(0, 120)}...</p>
            <p className="text-xs text-gray-500">{format(new Date(post.date), 'MMMM d, yyyy')}</p>
          </Card>
        ),
    },
    {
      id: 'resources-home',
      label: 'Public home',
      current: (frame) =>
        columnCard(
          frame,
          <Card hover>
            <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">{title}</h3>
            <p className="text-sm text-gray-600 mb-3 line-clamp-3">{excerpt.substring(0, 150)}...</p>
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>{format(new Date(post.date), 'MMMM d, yyyy')}</span>
              {post.featured_media > 0 && <span className="text-primary">Has Media</span>}
            </div>
          </Card>
        ),
    },
    {
      id: 'resources-practice',
      label: 'Practice home',
      current: () => (
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="min-h-[44px] px-4 py-3 text-sm text-gray-900">{title}</div>
        </div>
      ),
    },
    {
      id: 'resources-header',
      label: 'Header search',
      current: () => (
        <div className="flex items-start gap-3 p-4 bg-white border border-gray-200 rounded-lg">
          <DocumentTextIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" aria-hidden />
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-0.5">Resource</p>
            <h4 className="font-semibold text-gray-900 mb-1 line-clamp-1">{title}</h4>
            <p className="text-sm text-gray-600 line-clamp-2">{excerpt.substring(0, 100)}...</p>
          </div>
        </div>
      ),
    },
  ];

  return (
    <FamilyBlock
      id="review-resources"
      title="Resources"
      judge="Type rail draws a resource as a page. The title and save sit on the sheet. Date, Under revision, and the file count sit in a gray footer. The other options stay specific to this place. File chips on those options stay at the live size."
      destination="A resource card opens the document."
      notice={notice}
      error={error}
    >
      {examples.map((example) => (
        <ExampleSwitcher
          key={example.id}
          exampleId={example.id}
          label={example.label}
          options={exampleOptions(example.id)}
          choice={choices[example.id] ?? ''}
          onChoose={(optionId) => onChoose(example.id, optionId)}
          note={notes[example.id] ?? ''}
          onNote={(value) => onNote(example.id, value)}
          render={(optionId, frame) =>
            optionId === 'current' ? example.current(frame) : resourceScent(post, attachments, optionId, frame)
          }
        />
      ))}
    </FamilyBlock>
  );
}
